import * as Yup from 'yup'
import { FormikErrors, yupToFormErrors } from 'formik'
import { hasHtmlText } from '../../../../../../utils/sanitizeRichText'
import { DEMAND_CAPACITY_TABS, FINANCIAL_FUNDING_TABS, GOVERNANCE_POLICY_TABS, PROCUREMENT_STRATEGY_TABS, STEP_TABS } from './_models'

const requiredString = (label: string) =>
    Yup.string()
        .trim()
        .required()
        .label(label)

// A number input left empty holds '', which is missing rather than not a number.
const emptyAsUndefined = (value: unknown, original: unknown) => (original === '' ? undefined : value)

const requiredNumberInput = (label: string) =>
    Yup.number()
        .transform(emptyAsUndefined)
        .typeError(`${label} must be a number`)
        .required()
        .label(label)

const optionalNumberInput = (label: string) =>
    Yup.number()
        .transform(emptyAsUndefined)
        .typeError(`${label} must be a number`)
        .notRequired()

const requiredPercent = (label: string) =>
    requiredNumberInput(label)
        .min(0, `${label} must be between 0 and 100`)
        .max(100, `${label} must be between 0 and 100`)

// Editor HTML that must hold text, not just empty tags.
const requiredRichText = (label: string) =>
    Yup.string()
        .test('has-text', `${label} is a required field`, hasHtmlText)
        .label(label)

// Tab-wise steps save each tab on its own, so only the open tab is validated. Keyed by step, then tab id.
// Innovative Financing Mechanisms has no schema: none of its fields are required.
export const stepTabSchemas: Partial<Record<string, Record<number, Yup.AnyObjectSchema>>> = {
    // Nothing on either tab is required for the gate; the numbers only have to be in range.
    demand_and_capacity_validation: {
        [DEMAND_CAPACITY_TABS.base_case_counterfactual]: Yup.object({
            dcv_current_capacity: optionalNumberInput('Current Capacity').min(0, 'Current Capacity cannot be less than 0'),
            dcv_current_demand: optionalNumberInput('Current Demand').min(0, 'Current Demand cannot be less than 0'),
            dcv_current_service_gap: optionalNumberInput('Current Service Gap %').min(0, 'Current Service Gap % cannot be less than 0').max(100, 'Current Service Gap % cannot exceed 100'),
            dcv_current_cost_of_service: optionalNumberInput('Current Cost of Service').min(0, 'Current Cost of Service cannot be less than 0'),
        }),
        [DEMAND_CAPACITY_TABS.demand_validation]: Yup.object({
            dcv_future_demand_horizon_year: optionalNumberInput('Future Demand').min(1, 'Future Demand cannot be less than 1'),
            dcv_population_served: optionalNumberInput('Population Served').min(1, 'Population Served cannot be less than 1'),
            dcv_growth_rate: optionalNumberInput('Growth Rate').min(0, 'Growth Rate cannot be less than 0').max(100, 'Growth Rate cannot exceed 100'),
            dcv_peak_capacity_required: optionalNumberInput('Peak Capacity Required').min(1, 'Peak Capacity Required cannot be less than 1'),
        }),
    },
    // Risk Sharing Mechanism and the Economies of Scale summary are optional, so the summary tab
    // has no schema; neither has Supporting Documents.
    procurement_strategy: {
        [PROCUREMENT_STRATEGY_TABS.procurement_strategy_type]: Yup.object({
            procurement_strategy_type: Yup.number()
                .min(1, 'Select a BFI Alternative Financing Model')
                .required('Select a BFI Alternative Financing Model'),
        }),
        [PROCUREMENT_STRATEGY_TABS.supplier_market_maturity]: Yup.object({
            supplier_market_maturity: requiredString('Supplier Market Maturity'),
        }),
        [PROCUREMENT_STRATEGY_TABS.procurement_planning]: Yup.object({
            budget_statement: requiredRichText('Budget Statement'),
            procurement_procedure: requiredRichText('Procurement Procedure'),
            procurement_plan: requiredRichText('Procurement Plan'),
        }),
    },
    governance_policy_enabling_environment: {
        [GOVERNANCE_POLICY_TABS.governance]: Yup.object({
            government_policies_governance_role: requiredRichText('Government Policies & Governance Role'),
            governance_structure: requiredRichText('Governance Structure'),
            internal_controls_accountability: requiredRichText('Internal Controls & Accountability'),
        }),
        [GOVERNANCE_POLICY_TABS.institutional_arrangements]: Yup.object({
            agreements_dispute_resolution: requiredRichText('Agreements & Dispute Resolution'),
        }),
        [GOVERNANCE_POLICY_TABS.incentives_performance]: Yup.object({
            incentives_penalties_framework: requiredRichText('Incentives & Penalties Framework'),
        }),
        [GOVERNANCE_POLICY_TABS.policy_compliance]: Yup.object({
            national_policy_alignment: requiredRichText('National Policy Alignment'),
            regulatory_compliance_frameworks: Yup.array()
                .of(Yup.string())
                .min(1, 'Select at least one Regulatory Compliance Framework'),
            policy_alignment_summary_bfi_consistency: requiredRichText('Policy Alignment Summary & BFI Consistency'),
        }),
    },
    financial_and_funding_strategy_engine: {
        [FINANCIAL_FUNDING_TABS.funding_structure_revenue_model]: Yup.object({
            funding_secured: optionalNumberInput('Funding Secured'),
            funding_pending: optionalNumberInput('Funding Pending'),
            funding_gap: optionalNumberInput('Funding Gap'),
            private_sector_contribution: optionalNumberInput('Private Sector Contribution')
                .min(0, 'Cannot be less than 0')
                .max(100, 'Cannot exceed 100'),
            public_sector_contribution: optionalNumberInput('Public Sector Contribution')
                .min(0, 'Cannot be less than 0')
                .max(100, 'Cannot exceed 100'),
        }),
    },
}

// The first tab of a tab-wise step whose values fail its schema, with the errors, or null when all pass.
export async function findIncompleteStepTab(stepKey: string, values: object): Promise<{ tabId: number; title: string; errors: FormikErrors<object> } | null> {
    for (const { tabId, title } of STEP_TABS[stepKey] ?? []) {
        const schema = stepTabSchemas[stepKey]?.[tabId]
        if (!schema) continue
        try {
            await schema.validate(values, { abortEarly: false })
        } catch (error) {
            if (error instanceof Yup.ValidationError) {
                return { tabId, title, errors: yupToFormErrors(error) }
            }
            throw error
        }
    }
    return null
}


// const cbaRequiredNumber = (label: string) =>
//     Yup.number().when("benefit_monetisation_mode", {
//         is: "cba",
//         then: (schema) =>
//             schema
//                 .typeError(`${label} must be a number`)
//                 .required(`${label} is required`)
//                 .moreThan(0, `${label} must be greater than 0`),
//         otherwise: (schema) => schema.nullable(),
//     });

// const ceaRequiredNumber = (label: string) =>
//     Yup.number().when("benefit_monetisation_mode", {
//         is: "cea",
//         then: (schema) =>
//             schema
//                 .typeError(`${label} must be a number`)
//                 .required(`${label} is required`)
//                 .moreThan(0, `${label} must be greater than 0`),
//         otherwise: (schema) => schema.nullable(),
//     });


export const strategicResourcingStageSchema = {
    // Only the land answers are required for the gate.
    project_scope_resources_need: Yup.object({
        scope_land_property_is_land_identified: requiredString('Has the land been identified?'),
        scope_land_property_is_land_ownership_confirmed: requiredString('Has land ownership been confirmed?'),
        scope_land_property_is_zoning_compliant: requiredString('Is it zoning compliant?'),
    }),

    economies_of_scale_engine: Yup.object({
        economies_of_scale_assessment: requiredRichText('Economies of Scale Assessment'),
    }),

    special_procurement_actions_and_social_impact: Yup.object({
        gender_responsive_procurement: requiredRichText('Gender-responsive Procurement'),
        social_benefits_value_reference: requiredRichText('Social Benefits & Value (Reference)'),
        sustainability_and_environmental_compliance: requiredRichText('Sustainability and Environmental Compliance'),
        legal_and_technical_due_diligence: requiredRichText('Legal and Technical Due Diligence'),
    }),

    fiscal_sustainability_engine_ifsa: Yup.object({
        operating_cost_coverage: requiredPercent('Operating Cost Coverage'),
        lifecycle_cost_coverage: requiredPercent('Lifecycle Cost Coverage'),
        revenue_sufficiency: requiredPercent('Revenue Sufficiency'),
        fiscal_affordability_index: requiredPercent('Fiscal Affordability Index'),
        funding_gap_percentage: requiredPercent('Funding Gap'),
        debt_exposure: requiredNumberInput('Debt Exposure'),
        contingent_liability_exposure: requiredString('Contingent Liability Exposure')
    }),

    base_case_and_counterfactual_scenario_engine: Yup.object({
        counterfactual_scenario_type: Yup.array()
            .of(Yup.string())
            .min(1, 'Select at least one Counterfactual Scenario Type'),
        current_capacity: requiredString('Current Capacity'),
        base_case_current_demand: requiredString('Current Demand'),
        current_service_gap: Yup.number()
            .typeError('Current Service Gap % must be a number')
            .min(0, 'Cannot be less than 0')
            .max(100, 'Cannot exceed 100')
            .required()
            .label('Current Service Gap %'),
        current_cost_of_service: requiredString('Current Cost of Service'),
        consequences_of_no_intervention: Yup.array().of(Yup.string()),
        base_case_confidence_level: requiredString('Base Case Confidence Level'),
    }),
    // The whole step; the form uses stepTabSchemas for the open tab.
    demand_and_capacity_validation: Yup.object(),
    procurement_strategy: Yup.object(),
    governance_policy_enabling_environment: Yup.object(),
    financial_and_funding_strategy_engine: Yup.object()
}

export type IStrategicResourcingStageSchema =
    keyof typeof strategicResourcingStageSchema